import React, { useId } from "react";
import { KITCHEN_SWATCHES, lookupHex, lookupName } from "../data/designOptions";

/**
 * Realistic 3D-style kitchen mockup.
 * Axonometric perspective with depth, plus procedural textures
 * (wood grain, marble veining, tile mortar, brass, film grain) generated
 * with SVG `feTurbulence` + `feColorMatrix` so every swatch change is
 * instantly visible without depending on external images.
 */
const KitchenMockup = ({ config, className = "" }) => {
  const uid = useId().replace(/:/g, "");
  const cabinet = lookupHex(KITCHEN_SWATCHES.cabinet, config.cabinet, "#F3F1EC");
  const countertop = lookupHex(KITCHEN_SWATCHES.countertop, config.countertop, "#EEEAE0");
  const backsplash = lookupHex(KITCHEN_SWATCHES.backsplash, config.backsplash, "#EFE7D6");
  const wall = lookupHex(KITCHEN_SWATCHES.wall, config.wall, "#EAE2D2");
  const handle = lookupHex(KITCHEN_SWATCHES.handle, config.handle, "#B8996F");
  const wood = lookupHex(KITCHEN_SWATCHES.wood, config.wood, "#C9A57A");
  const isGloss = config.finish === "gloss";

  const cd = (h, p) => shade(h, -p);
  const cl = (h, p) => shade(h, p);

  // Unique IDs for SVG defs
  const id = (s) => `k${uid}${s}`;

  return (
    <div className={`relative w-full ${className}`} data-testid="kitchen-svg-mockup">
      <svg
        viewBox="0 0 1200 760"
        xmlns="http://www.w3.org/2000/svg"
        style={{ display: "block", width: "100%", height: "auto" }}
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          {/* === ROOM === */}
          <radialGradient id={id("Amb")} cx="50%" cy="0%" r="100%">
            <stop offset="0%" stopColor={cl(wall, 16)} />
            <stop offset="55%" stopColor={wall} />
            <stop offset="100%" stopColor={cd(wall, 22)} />
          </radialGradient>
          <linearGradient id={id("Side")} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={cd(wall, 32)} />
            <stop offset="100%" stopColor={cd(wall, 14)} />
          </linearGradient>

          {/* === WOOD GRAIN TEXTURE === */}
          <filter id={id("WoodTex")} x="0%" y="0%" width="100%" height="100%">
            <feTurbulence type="turbulence" baseFrequency="0.012 0.32" numOctaves="2" seed="3" result="t" />
            <feColorMatrix in="t" type="matrix" values={`
              0 0 0 0 ${cTo(cd(wood, 38))}
              0 0 0 0 ${cTo(cd(wood, 30))}
              0 0 0 0 ${cTo(cd(wood, 28))}
              0 0 0 0.55 0`} />
            <feComposite in2="SourceGraphic" operator="in" />
            <feComposite in="SourceGraphic" operator="arithmetic" k1="0" k2="1" k3="1" k4="0" />
          </filter>
          <linearGradient id={id("WoodBase")} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={cl(wood, 12)} />
            <stop offset="100%" stopColor={cd(wood, 18)} />
          </linearGradient>

          {/* === MARBLE VEINING === */}
          <filter id={id("Marble")} x="0%" y="0%" width="100%" height="100%">
            <feTurbulence type="fractalNoise" baseFrequency="0.014 0.04" numOctaves="3" seed="7" result="t" />
            <feDisplacementMap in="SourceGraphic" in2="t" scale="3" />
          </filter>
          <linearGradient id={id("CtBase")} x1="0" y1="0" x2="1" y2="0.5">
            <stop offset="0%" stopColor={cl(countertop, 12)} />
            <stop offset="50%" stopColor={countertop} />
            <stop offset="100%" stopColor={cd(countertop, 8)} />
          </linearGradient>
          <pattern id={id("MarbleVein")} x="0" y="0" width="360" height="40" patternUnits="userSpaceOnUse">
            <path d="M 0 22 Q 60 14 140 24 T 280 22 T 360 18" fill="none" stroke={cd(countertop, 38)} strokeOpacity="0.5" strokeWidth="0.7" />
            <path d="M 0 32 Q 90 28 180 34 T 360 30" fill="none" stroke={cd(countertop, 45)} strokeOpacity="0.32" strokeWidth="0.5" />
            <path d="M 0 8 Q 120 4 240 12 T 360 6" fill="none" stroke={cd(countertop, 30)} strokeOpacity="0.3" strokeWidth="0.4" />
          </pattern>

          {/* === CABINET FACE === */}
          <linearGradient id={id("CabFace")} x1="0" y1="0" x2="0.05" y2="1">
            <stop offset="0%" stopColor={cl(cabinet, 14)} />
            <stop offset="55%" stopColor={cabinet} />
            <stop offset="100%" stopColor={cd(cabinet, 18)} />
          </linearGradient>
          <linearGradient id={id("CabSide")} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={cd(cabinet, 34)} />
            <stop offset="100%" stopColor={cd(cabinet, 18)} />
          </linearGradient>
          <linearGradient id={id("CabTop")} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={cl(cabinet, 20)} />
            <stop offset="100%" stopColor={cabinet} />
          </linearGradient>

          {/* === BACKSPLASH === */}
          <linearGradient id={id("Bs")} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={cl(backsplash, 8)} />
            <stop offset="100%" stopColor={cd(backsplash, 10)} />
          </linearGradient>
          <pattern id={id("BsTile")} x="0" y="0" width="80" height="40" patternUnits="userSpaceOnUse">
            <rect width="80" height="40" fill={backsplash} />
            <line x1="0" y1="20" x2="80" y2="20" stroke={cd(backsplash, 30)} strokeOpacity="0.45" strokeWidth="0.6" />
            <line x1="40" y1="0" x2="40" y2="20" stroke={cd(backsplash, 30)} strokeOpacity="0.45" strokeWidth="0.6" />
            <line x1="0" y1="20" x2="0" y2="40" stroke={cd(backsplash, 30)} strokeOpacity="0.45" strokeWidth="0.6" />
            <line x1="80" y1="20" x2="80" y2="40" stroke={cd(backsplash, 30)} strokeOpacity="0.45" strokeWidth="0.6" />
          </pattern>

          {/* === BRASS === */}
          <linearGradient id={id("Brass")} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={cl(handle, 26)} />
            <stop offset="48%" stopColor={cl(handle, 8)} />
            <stop offset="52%" stopColor={cd(handle, 4)} />
            <stop offset="100%" stopColor={cd(handle, 22)} />
          </linearGradient>

          {/* === FLOOR (dark wood) === */}
          <linearGradient id={id("Floor")} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#231a13" />
            <stop offset="100%" stopColor="#0a0706" />
          </linearGradient>
          <linearGradient id={id("FloorSheen")} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(255,255,255,0.05)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
          </linearGradient>

          {/* === LIGHT CONES === */}
          <linearGradient id={id("Light")} x1="0" y1="0" x2="0.2" y2="1">
            <stop offset="0%" stopColor="rgba(255,236,196,0.26)" />
            <stop offset="100%" stopColor="rgba(255,236,196,0)" />
          </linearGradient>
          <radialGradient id={id("Glow")} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(255,220,160,0.6)" />
            <stop offset="100%" stopColor="rgba(255,220,160,0)" />
          </radialGradient>

          {/* === GLOSS === */}
          {isGloss && (
            <linearGradient id={id("Gloss")} x1="0" y1="0" x2="0.18" y2="1">
              <stop offset="0%" stopColor="rgba(255,255,255,0.42)" />
              <stop offset="30%" stopColor="rgba(255,255,255,0.08)" />
              <stop offset="100%" stopColor="rgba(255,255,255,0)" />
            </linearGradient>
          )}

          {/* === FILM GRAIN === */}
          <filter id={id("Grain")} x="0%" y="0%" width="100%" height="100%">
            <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" seed="11" />
            <feColorMatrix type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.08 0" />
          </filter>

          {/* === AMBIENT OCCLUSION DROP SHADOW === */}
          <filter id={id("AO")} x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="8" />
            <feOffset dx="0" dy="10" />
            <feComponentTransfer><feFuncA type="linear" slope="0.6" /></feComponentTransfer>
            <feMerge>
              <feMergeNode />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id={id("Blur")}><feGaussianBlur stdDeviation="14" /></filter>

          {/* Vignette */}
          <radialGradient id={id("Vig")} cx="50%" cy="55%" r="70%">
            <stop offset="60%" stopColor="rgba(0,0,0,0)" />
            <stop offset="100%" stopColor="rgba(0,0,0,0.45)" />
          </radialGradient>
        </defs>

        {/* === ROOM SHELL === */}
        <polygon points="120,40 1080,40 1080,470 120,470" fill={`url(#${id("Amb")})`} data-testid="kitchen-wall" />
        <polygon points="0,0 120,40 120,600 0,720" fill={`url(#${id("Side")})`} />
        <polygon points="1200,0 1080,40 1080,600 1200,720" fill={`url(#${id("Side")})`} transform="scale(-1,1) translate(-1200,0)" />
        <polygon points="0,720 1200,720 1080,470 120,470" fill={`url(#${id("Floor")})`} />
        <polygon points="0,720 1200,720 1080,470 120,470" fill={`url(#${id("FloorSheen")})`} />
        {[140, 270, 400, 530, 660, 790, 920, 1050].map((x) => (
          <line key={`fp-${x}`} x1={x} y1="720" x2={120 + (x / 1200) * 960} y2="470" stroke="rgba(255,255,255,0.045)" strokeWidth="1" />
        ))}

        {/* WINDOW + light cone */}
        <g>
          <rect x="820" y="80" width="240" height="220" fill="#0e0d0b" />
          <rect x="836" y="96" width="208" height="188" fill="#d2c8b1" opacity="0.7" />
          <line x1="940" y1="96" x2="940" y2="284" stroke="#0e0d0b" strokeWidth="3" />
          <line x1="836" y1="190" x2="1044" y2="190" stroke="#0e0d0b" strokeWidth="3" />
          <polygon points="836,284 1044,284 1080,520 720,520" fill={`url(#${id("Light")})`} opacity="0.85" />
        </g>

        {/* Wall art */}
        <g opacity="0.95">
          <rect x="180" y="120" width="180" height="220" fill={cd(wall, 35)} />
          <rect x="190" y="130" width="160" height="200" fill={cd(handle, 35)} />
          <rect x="208" y="148" width="124" height="164" fill={cl(handle, 22)} opacity="0.85" />
        </g>

        {/* UPPER CABINETS */}
        <g data-testid="kitchen-upper-cabinets" filter={`url(#${id("AO")})`}>
          {/* Top */}
          <polygon points="170,165 990,165 1010,180 190,180" fill={`url(#${id("CabTop")})`} />
          {/* Front face */}
          <rect x="170" y="180" width="820" height="170" fill={`url(#${id("CabFace")})`} />
          {/* Right side depth */}
          <polygon points="990,180 1010,180 1010,360 990,350" fill={`url(#${id("CabSide")})`} />
          {/* Door dividers */}
          {[340, 510, 680, 850].map((x) => (
            <line key={`div-${x}`} x1={x} y1="180" x2={x} y2="350" stroke={cd(cabinet, 36)} strokeWidth="1.5" />
          ))}
          {/* Inset panel shadow */}
          {[170, 340, 510, 680, 850].map((x) => (
            <rect key={`ip-${x}`} x={x + 8} y="190" width="154" height="150" fill="none" stroke={cd(cabinet, 22)} strokeOpacity="0.35" strokeWidth="0.8" />
          ))}
          {/* Recessed brass handle strip */}
          {[255, 425, 595, 765, 935].map((x) => (
            <rect key={`uh-${x}`} x={x - 20} y="334" width="40" height="3.5" rx="1" fill={`url(#${id("Brass")})`} />
          ))}
          {isGloss && <rect x="170" y="180" width="820" height="170" fill={`url(#${id("Gloss")})`} />}
        </g>

        {/* BACKSPLASH with real tile pattern */}
        <g data-testid="kitchen-backsplash">
          <rect x="170" y="350" width="820" height="80" fill={`url(#${id("BsTile")})`} />
          {/* light spill */}
          <rect x="720" y="350" width="270" height="80" fill={`url(#${id("Light")})`} opacity="0.45" />
        </g>

        {/* COUNTERTOP (slab) with marble vein pattern */}
        <g filter={`url(#${id("AO")})`} data-testid="kitchen-countertop">
          {/* Top */}
          <polygon points="150,430 1010,430 1030,448 170,448" fill={`url(#${id("CtBase")})`} />
          <polygon points="150,430 1010,430 1030,448 170,448" fill={`url(#${id("MarbleVein")})`} opacity="0.75" />
          {/* Front edge */}
          <rect x="170" y="448" width="860" height="14" fill={cd(countertop, 18)} />
          {/* Right return */}
          <polygon points="1010,430 1030,448 1030,462 1010,448" fill={cd(countertop, 25)} />
        </g>

        {/* LOWER CABINETS */}
        <g data-testid="kitchen-lower-cabinets" filter={`url(#${id("AO")})`}>
          {/* Wood island front */}
          <rect x="170" y="462" width="340" height="220" fill={`url(#${id("WoodBase")})`} />
          {/* Wood grain overlay via turbulence (filtered rect) */}
          <rect x="170" y="462" width="340" height="220" fill={cl(wood, 5)} filter={`url(#${id("WoodTex")})`} opacity="0.65" />
          {/* Right side depth */}
          <polygon points="510,462 530,448 530,668 510,682" fill={cd(wood, 30)} />
          {/* drawer splits */}
          <line x1="170" y1="530" x2="510" y2="530" stroke={cd(wood, 45)} strokeWidth="1.5" />
          <line x1="170" y1="600" x2="510" y2="600" stroke={cd(wood, 45)} strokeWidth="1.5" />
          {/* handles */}
          {[492, 562, 632].map((y) => (
            <rect key={`wh-${y}`} x="285" y={y} width="110" height="5" rx="2" fill={`url(#${id("Brass")})`} />
          ))}

          {/* Painted base */}
          <rect x="510" y="462" width="500" height="220" fill={`url(#${id("CabFace")})`} />
          <polygon points="1010,462 1030,448 1030,662 1010,682" fill={`url(#${id("CabSide")})`} />
          {[680, 845].map((x) => (
            <line key={`bd-${x}`} x1={x} y1="462" x2={x} y2="682" stroke={cd(cabinet, 38)} strokeWidth="1.5" />
          ))}
          {[510, 680, 845].map((x) => (
            <rect key={`bip-${x}`} x={x + 10} y="478" width="150" height="190" fill="none" stroke={cd(cabinet, 22)} strokeOpacity="0.35" strokeWidth="0.8" />
          ))}
          {[595, 762, 927].map((x) => (
            <rect key={`bh-${x}`} x={x - 25} y="560" width="50" height="5" rx="2" fill={`url(#${id("Brass")})`} />
          ))}
          {isGloss && <rect x="510" y="462" width="500" height="220" fill={`url(#${id("Gloss")})`} />}
        </g>

        {/* SINK */}
        <g>
          <rect x="680" y="432" width="120" height="18" rx="2" fill={cd(countertop, 55)} opacity="0.7" />
          <rect x="688" y="436" width="104" height="10" rx="1" fill="#070707" opacity="0.85" />
          <path d="M 738 432 L 738 410 Q 738 402 746 402 L 762 402" fill="none" stroke={`url(#${id("Brass")})`} strokeWidth="3" strokeLinecap="round" />
          <circle cx="762" cy="402" r="5" fill={`url(#${id("Brass")})`} />
        </g>

        {/* STOVE TOP */}
        <g>
          <rect x="850" y="432" width="150" height="18" rx="2" fill="#0a0a0a" opacity="0.92" />
          {[885, 925, 965].map((cx) => (
            <g key={`burner-${cx}`}>
              <circle cx={cx} cy="441" r="9" fill="#141414" stroke={`url(#${id("Brass")})`} strokeWidth="0.8" />
              <circle cx={cx} cy="441" r="4" fill="#1f1f1f" />
            </g>
          ))}
        </g>

        {/* PENDANTS with glow */}
        {[[280, 150], [430, 170], [580, 150]].map(([x, y]) => (
          <g key={`pen-${x}`}>
            <line x1={x} y1="0" x2={x} y2={y - 14} stroke="#241e18" strokeWidth="1.8" />
            <circle cx={x} cy={y + 30} r="60" fill={`url(#${id("Glow")})`} opacity="0.7" />
            <ellipse cx={x} cy={y} rx="20" ry="14" fill={`url(#${id("Brass")})`} />
            <ellipse cx={x} cy={y - 4} rx="20" ry="6" fill={cl(handle, 32)} />
            <ellipse cx={x - 4} cy={y - 2} rx="6" ry="2.4" fill="rgba(255,255,255,0.55)" />
          </g>
        ))}

        {/* COUNTER DECOR */}
        <g>
          <rect x="200" y="412" width="80" height="14" rx="2" fill={cl(wood, 18)} />
          <rect x="200" y="412" width="80" height="3" fill={cl(wood, 30)} />
          <rect x="320" y="400" width="22" height="28" rx="2" fill={cl(cabinet, 10)} />
          <rect x="318" y="396" width="26" height="6" rx="2" fill={`url(#${id("Brass")})`} />
          <rect x="370" y="406" width="28" height="22" fill={cd(cabinet, 30)} />
          <ellipse cx="384" cy="406" rx="22" ry="8" fill="#3a4a32" />
          <ellipse cx="378" cy="402" rx="10" ry="6" fill="#4c5e40" />
          <ellipse cx="394" cy="404" rx="10" ry="6" fill="#3e503a" />
        </g>

        {/* Ground contact shadow */}
        <ellipse cx="600" cy="688" rx="500" ry="10" fill="black" opacity="0.55" filter={`url(#${id("Blur")})`} />

        {/* Vignette */}
        <rect x="0" y="0" width="1200" height="760" fill={`url(#${id("Vig")})`} pointerEvents="none" />

        {/* Film grain overlay */}
        <rect x="0" y="0" width="1200" height="760" filter={`url(#${id("Grain")})`} pointerEvents="none" opacity="0.5" />
      </svg>

      {/* Minimal material strip */}
      <MaterialStrip
        items={[
          { label: "Cabinet", name: lookupName(KITCHEN_SWATCHES.cabinet, config.cabinet), tone: cabinet },
          { label: "Counter", name: lookupName(KITCHEN_SWATCHES.countertop, config.countertop), tone: countertop },
          { label: "Backsplash", name: lookupName(KITCHEN_SWATCHES.backsplash, config.backsplash), tone: backsplash },
          { label: "Wood", name: lookupName(KITCHEN_SWATCHES.wood, config.wood), tone: wood },
          { label: "Handle", name: lookupName(KITCHEN_SWATCHES.handle, config.handle), tone: handle },
          { label: "Finish", name: config.finish === "gloss" ? "Gloss" : "Matte" },
        ]}
      />
    </div>
  );
};

export const MaterialStrip = ({ items }) => (
  <div
    className="pointer-events-none absolute bottom-2 left-2 right-2 flex flex-wrap gap-1.5"
    data-testid="material-chips"
  >
    {items.map((it) => (
      <div
        key={it.label}
        className="flex items-center gap-1.5 bg-black/55 px-2 py-1 text-[9px] uppercase tracking-[0.16em] text-white backdrop-blur"
      >
        {it.tone && (
          <span aria-hidden className="inline-block h-2.5 w-2.5 ring-1 ring-white/30" style={{ backgroundColor: it.tone }} />
        )}
        <span className="text-white/55">{it.label}</span>
        <span className="text-white">{it.name}</span>
      </div>
    ))}
  </div>
);

function shade(hex, percent) {
  if (!hex || !hex.startsWith("#")) return hex;
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const num = parseInt(h, 16);
  let r = (num >> 16) + percent;
  let g = ((num >> 8) & 0x00ff) + percent;
  let b = (num & 0x0000ff) + percent;
  r = Math.max(0, Math.min(255, r));
  g = Math.max(0, Math.min(255, g));
  b = Math.max(0, Math.min(255, b));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

function cTo(hex) {
  if (!hex || !hex.startsWith("#")) return "0";
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  return (parseInt(h.slice(0, 2), 16) / 255).toFixed(3);
}

export default KitchenMockup;
